import React from 'react';
import cx from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@/storybook/icons/DragHandleIcon';
import styles from '../AdjustTableDialog.module.css';

export type FlatItem = {
  id: string;
  label: string;
  parentId: string | null;
};

type SortableItemProps = {
  item: FlatItem;
  isDropDisallowed?: boolean;
  onHoverGroup?: (groupId: string | null) => void;
  isDimmed?: boolean;
};

const SortableItem = React.memo(
  ({ item, isDropDisallowed = false, onHoverGroup, isDimmed = false }: SortableItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
    const isChild = item.parentId !== null;

    const verticalTransform = transform ? { ...transform, x: 0 } : null;

    const itemStyle = {
      transform: CSS.Transform.toString(verticalTransform),
      transition,
      position: 'relative' as const,
      zIndex: isDragging ? 1 : 0,
    };

    const handleMouseEnter = () => onHoverGroup?.(isChild ? item.parentId : null);

    return (
      <li
        ref={setNodeRef}
        style={itemStyle}
        className={cx(isChild ? styles.childItem : styles.columnItem, {
          [styles.columnItemDragging]: isDragging && !isChild,
          [styles.childItemDragging]: isDragging && isChild,
          [styles.dropDisallowed]: isDragging && isDropDisallowed,
          [styles.dimmedText]: isDimmed && !isDragging,
        })}
        onMouseEnter={handleMouseEnter}
      >
        <span className={styles.dragHandleArea} {...attributes} {...listeners}>
          <DragHandleIcon className={isChild ? styles.childDragHandle : styles.dragHandle} />
        </span>
        {item.label}
      </li>
    );
  },
);

SortableItem.displayName = 'SortableItem';

export default SortableItem;
